import type { UseFormGetValues, UseFormSetValue, FieldValues } from "react-hook-form";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { formatDate } from "@/lib/formatDate";
import { PrescriptionDataTransformer, type Prescription } from "@/types/prescription";

/**
 * Loose medicine-name key: ignores case, punctuation, dosage-form words and a unit after the strength,
 * so "Warf 1", "warf-1 tab" and the catalog's "Warf 1 Tablet" compare equal.
 */
const FORM_WORDS = /\b(tablets?|tabs?|capsules?|caps?|syrups?|syp|suspensions?|susp|drops?|injections?|inj|creams?|ointments?|oint|gels?|lotions?|solutions?|sachets?|powders?|inhalers?|sprays?)\b/g;
export const nameKey = (v?: string | null) => (v ?? "").toLowerCase()
    .replace(/[^a-z0-9.]+/g, " ")
    .replace(FORM_WORDS, " ")
    .replace(/(\d)\s*mg\b/g, "$1")
    .replace(/\s+/g, " ").trim();
export const sameName = (a?: string | null, b?: string | null) => nameKey(a) !== "" && nameKey(a) === nameKey(b);

type Row = { product?: string | null; brand_name?: string | null };
const filledRows = (rows: Row[] | undefined) => (rows || []).filter(m => m?.product?.trim() || m?.brand_name?.trim());

/** The last prescription's medicines that aren't on the pad yet (copy-ready rows) */
export function medicinesToRepeat(last: Prescription | undefined, current: Row[] | undefined) {
    if (!last) return [];
    const onPad = filledRows(current);
    return PrescriptionDataTransformer.toCopyData(last, last.patient_id, last.doctor_id, last.clinic_id).medicines
        .filter(m => m.product?.trim() && !onPad.some(r => sameName(r.product || r.brand_name, m.product)));
}

/**
 * Adds the last prescription's medicines below the ones already on the pad, skipping any already there.
 * They go through the same allergy / interaction / dose checks as typed ones; the toast offers Undo.
 */
export function repeatLastMedicines(last: Prescription | undefined, getValues: UseFormGetValues<FieldValues>, setValue: UseFormSetValue<FieldValues>) {
    const before = getValues("medicines") || [];
    const incoming = medicinesToRepeat(last, before);
    if (!last || !incoming.length) return;
    setValue("medicines", [...filledRows(before), ...incoming], { shouldDirty: true, shouldTouch: true });
    toast({
        title: `Added ${incoming.length} medicine${incoming.length > 1 ? "s" : ""} from ${formatDate(last.date)}`,
        description: "Check doses before saving.",
        action: (
            <ToastAction altText="Undo adding medicines" onClick={() => setValue("medicines", before, { shouldDirty: true })}>
                Undo
            </ToastAction>
        ),
    });
}
